import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { genUUID } from '../lib/uuid';
import { burnRegionsOntoImage, burnDetectedObjects } from '../lib/regionBurn';
import {
  ArrowLeft, ChevronDown, Download, Share, AlertCircle, Sparkles, ImageIcon,
  CoArchitectLogo, Home, Trash, Sofa, RefreshCw,
} from '../components/Icons';
import { API, useAuth } from '../contexts/AuthContext';
import { ImagePicker, MultiImagePicker, PickedImage } from '../components/ImagePicker';
import { ImageAnnotator, AnnotationResult, LabeledRegion } from '../components/ImageAnnotator';
import { detectMode, InstructionMode } from '../utils/instructionMode';
import { ObjectSelector } from '../components/ObjectSelector';
import { detectObjects, shoppableLabels, DetectedObject } from '../lib/detectApi';
import { mixpanel } from '../lib/mixpanel';
import { ProductPanel } from '../components/ProductPanel';
import { Product } from '../lib/productsApi';

/* ─── Types ──────────────────────────────────────────────────── */
interface Generation {
  id: string;
  parentGenerationId?: string | null;
  productDescription: string;
  userInstructions: string;
  resultImageUrl?: string;
  referenceImageUrls?: string[];
  status: 'pending' | 'processing' | 'completed' | 'failed';
  hasAnnotation?: boolean;
  createdAt: string;
  updatedAt?: string;
}

interface Project {
  id: string;
  name: string;
  originalImageUrl?: string;
  createdAt: string;
  generations: Generation[];
}

/* ─── Helpers ─────────────────────────────────────────────────── */

// Thrown by handleChatSubmit on 402 once the out-of-credits sheet is already
// shown — signals ThreadCard's catch to reset silently instead of rendering
// a raw axios error string in the chat's inline alert.
class HandledGenerationError extends Error {}

async function downloadImage(url: string, filename: string, imageType: 'original' | 'root' | 'followup') {
  mixpanel.track('image_downloaded', { image_type: imageType });
  try {
    const res  = await fetch(url);
    const blob = await res.blob();

    // iOS Safari ignores <a download>; use Web Share API so the user can
    // pick "Save Image" from the native share sheet.
    const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
    const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
    if (nav.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file] });
        return;
      } catch (err) {
        if ((err as DOMException)?.name === 'AbortError') return;
        // fall through to anchor-download fallback
      }
    }

    const href = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(href);
  } catch {
    window.open(url, '_blank');
  }
}

async function shareImage(url: string, filename: string, generationId: string, generationType: 'root' | 'followup') {
  const fire = (shareMethod: 'native_file' | 'download_fallback') =>
    mixpanel.track('image_shared', { generation_id: generationId, generation_type: generationType, share_method: shareMethod });

  // Fetch the image bytes through our own authenticated API instead of the
  // raw storage URL. The storage bucket's policy is public-read but has no
  // CORS configuration, so a direct cross-origin fetch(url) throws in the
  // browser — this endpoint is same-origin-safe and already used elsewhere
  // for client-side image processing (see the object-marking flow above).
  let blob: Blob;
  try {
    const token = localStorage.getItem('accessToken');
    const res = await fetch(`${API}/generations/${generationId}/image`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error('Failed to fetch image');
    blob = await res.blob();
  } catch {
    // Couldn't retrieve the image bytes at all — nothing to share.
    window.open(url, '_blank');
    return;
  }

  const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
  const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };

  // We always want to hand apps the actual image, never just a link — a
  // shared URL renders as a bare text link in Messages/WhatsApp/etc.,
  // not the preview photo itself.
  if (nav.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: 'Co-Architect AI Preview' });
      fire('native_file');
      return;
    } catch (err) {
      if ((err as DOMException)?.name === 'AbortError') return;
      // fall through to the download fallback below
    }
  }

  // No native file-sharing support (e.g. most desktop browsers) — save the
  // image locally so the user can attach the actual file themselves.
  const href = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(href);
  fire('download_fallback');
}

const MAX_REFERENCE_IMAGES = 5;

/** Short noun for a product, used to phrase the swap/add quick-prompt chips. */
function productWord(p: Product): string {
  const raw = (p.productType || p.category || '').trim();
  return raw ? raw.toLowerCase() : 'item';
}

/**
 * Sent when the instruction box is left blank with a product reference
 * attached — the backend requires non-empty text, but a product photo is
 * self-explanatory enough that the user shouldn't have to type anything.
 * Deliberately additive/non-destructive, since we don't know what (if
 * anything) they mean to replace.
 */
const DEFAULT_PRODUCT_INSTRUCTION =
  'Add the reference product(s) into the room, matching their placement, scale and lighting to the space. Leave everything else unchanged.';

/** Fetch an external image through the proxy and wrap it as a PickedImage. */
async function fetchAsPickedImage(url: string, filenameBase: string): Promise<PickedImage> {
  const resp = await fetch(`${API}/proxy/image?url=${encodeURIComponent(url)}`);
  if (!resp.ok) throw new Error('proxy failed');
  const blob = await resp.blob();
  const ext = blob.type.includes('png') ? 'png' : 'jpg';
  const file = new File([blob], `${filenameBase}.${ext}`, { type: blob.type });
  return { file, url: URL.createObjectURL(blob) };
}

/** Build a linear thread chain: root → child → grandchild … */
function buildThread(generations: Generation[], rootId: string): Generation[] {
  const chain: Generation[] = [];
  let currentId: string | undefined = rootId;
  while (currentId) {
    const gen = generations.find(g => g.id === currentId);
    if (!gen) break;
    chain.push(gen);
    const child = generations.find(g => g.parentGenerationId === currentId);
    currentId = child?.id;
  }
  return chain;
}


/* ─── Image Modal ─────────────────────────────────────────────── */
function ImageModal({ image, onClose }: { image: { url: string; alt: string } | null; onClose: () => void }) {
  if (!image) return null;
  const isAiImage = image.alt.startsWith('AI') || image.alt.startsWith('Iteration');
  return (
    <div className="sheet-backdrop center" onClick={onClose} style={{ padding: 16 }}>
      <div className="image-lightbox" onClick={e => e.stopPropagation()}>
        <img src={image.url} alt={image.alt} />
        <button className="image-lightbox-close" onClick={onClose} aria-label="Close">✕</button>
        {isAiImage && <div className="preview-watermark">Co-Architect</div>}
      </div>
    </div>
  );
}

/* ─── Types ──────────────────────────────────────────────────── */
export interface BeforeOption {
  url: string;
  label: string;
}

/* ─── Before / After Slider ──────────────────────────────────── */
function BeforeAfterSlider({
  beforeUrl,
  afterUrl,
  beforeOptions,
  onImageClick,
}: {
  beforeUrl?: string;
  afterUrl: string;
  beforeOptions?: BeforeOption[];
  onImageClick: (image: { url: string; alt: string }) => void;
}) {
  const [sliderX, setSliderX] = useState(50);
  const [selectedBefore, setSelectedBefore] = useState(beforeUrl);
  const [pickerOpen, setPickerOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const dragMoved = useRef(false);
  const startClientX = useRef(0);

  // Sync if the default beforeUrl changes (e.g. polling completes)
  useEffect(() => { setSelectedBefore(prev => prev ?? beforeUrl); }, [beforeUrl]);

  const updatePos = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pct = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    setSliderX(pct);
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      if (Math.abs(e.clientX - startClientX.current) > 3) dragMoved.current = true;
      updatePos(e.clientX);
    };
    const onMouseUp = () => { dragging.current = false; };
    const onTouchMove = (e: TouchEvent) => {
      if (!dragging.current) return;
      if (Math.abs(e.touches[0].clientX - startClientX.current) > 3) dragMoved.current = true;
      updatePos(e.touches[0].clientX);
    };
    const onTouchEnd = () => { dragging.current = false; };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [updatePos]);

  const handlePointerDown = (clientX: number) => {
    dragging.current = true;
    dragMoved.current = false;
    startClientX.current = clientX;
  };

  const handleClick = (e: React.MouseEvent) => {
    if (dragMoved.current) return;
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const clickPct = ((e.clientX - rect.left) / rect.width) * 100;
    const selLabel = beforeOptions?.find(o => o.url === selectedBefore)?.label ?? 'Before';
    if (clickPct <= sliderX && selectedBefore) {
      onImageClick({ url: selectedBefore, alt: selLabel });
    } else if (clickPct > sliderX) {
      onImageClick({ url: afterUrl, alt: 'AI Preview' });
    }
  };

  // Options available as "before" — exclude the current "after" image
  const pickerOptions = beforeOptions?.filter(o => o.url !== afterUrl) ?? [];

  return (
    <div className="slider-with-picker">
      <div
        ref={containerRef}
        className="preview-slider"
        onMouseDown={e => handlePointerDown(e.clientX)}
        onTouchStart={e => handlePointerDown(e.touches[0].clientX)}
        onClick={handleClick}
      >
        <img src={afterUrl} className="slider-img" alt="AI Preview" draggable={false} />
        {selectedBefore ? (
          <img
            src={selectedBefore}
            className="slider-img"
            style={{ clipPath: `inset(0 ${100 - sliderX}% 0 0)` }}
            alt="Before"
            draggable={false}
          />
        ) : (
          <div
            className="slider-img"
            style={{ clipPath: `inset(0 ${100 - sliderX}% 0 0)`, background: 'var(--surface-alt)' }}
          />
        )}
        <div className="slider-divider" style={{ left: `${sliderX}%` }} />
        <div className="slider-handle" style={{ left: `${sliderX}%` }}>
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
            <polyline points="7,4 3,10 7,16" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <polyline points="13,4 17,10 13,16" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <span className="slider-badge slider-badge-before">BEFORE</span>
        <span className="slider-badge slider-badge-after">AFTER</span>
        {sliderX === 50 && (
          <div className="slider-hint">← drag to compare →</div>
        )}
        <div className="preview-watermark">Co-Architect</div>
      </div>

      {/* Before-image picker — collapsed by default, toggled by the compare icon */}
      {pickerOptions.length > 1 && (
        <div className="before-picker">
          <button
            type="button"
            className={`before-picker-toggle${pickerOpen ? ' before-picker-toggle--open' : ''}`}
            onClick={() => setPickerOpen(o => !o)}
            aria-label="Compare with"
            aria-expanded={pickerOpen}
            title="Compare with"
          >
            <CompareIcon />
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: pickerOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {pickerOpen && (
            <div className="before-picker-scroll">
              {pickerOptions.map(opt => (
                <button
                  key={opt.url}
                  type="button"
                  className={`before-picker-thumb${selectedBefore === opt.url ? ' before-picker-thumb--active' : ''}`}
                  onClick={() => setSelectedBefore(opt.url)}
                  title={opt.label}
                >
                  <img src={opt.url} alt={opt.label} />
                  <span className="before-picker-thumb-label">{opt.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Compare icon ───────────────────────────────────────────── */
function CompareIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="3" x2="12" y2="21" />
      <polyline points="8,8 4,12 8,16" />
      <polyline points="16,8 20,12 16,16" />
    </svg>
  );
}

/* ─── Send icon ──────────────────────────────────────────────── */
function SendIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

/* ─── Thread Card ─────────────────────────────────────────────── */
interface ThreadCardProps {
  thread: Generation[];
  projectId: string;
  originalImageUrl?: string;
  beforeOptions: BeforeOption[];
  isExpanded: boolean;
  onToggle: () => void;
  onImageClick: (image: { url: string; alt: string }) => void;
  onChatSubmit: (
    parentId: string,
    instruction: string,
    mode: InstructionMode,
    refImages: PickedImage[],
    hasAnnotation: boolean,
    inheritRefs: boolean,
    annotatedImage?: File,
  ) => Promise<void>;
  onDelete: (rootId: string) => Promise<void>;
  onRetry: (generationId: string) => Promise<void>;
}

function AttachIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
      <circle cx="8.5" cy="8.5" r="1.5"/>
      <polyline points="21 15 16 10 5 21"/>
    </svg>
  );
}

function ThreadCard({
  thread, projectId, originalImageUrl, beforeOptions, isExpanded, onToggle, onImageClick, onChatSubmit, onDelete, onRetry,
}: ThreadCardProps) {
  const rootGen = thread[0];
  const lastGen = thread[thread.length - 1];
  const isBusy  = lastGen.status === 'processing' || lastGen.status === 'pending';
  const canChat  = lastGen.status === 'completed';

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting]           = useState(false);

  const [chatInput, setChatInput]         = useState('');
  const [chatRefImages, setChatRefImages] = useState<PickedImage[]>([]);

  // Reference images from the nearest earlier turn (or the root) — reused by
  // the backend on follow-ups unless the user dismisses the chips.
  const ancestorRefUrls = useMemo(() => {
    for (let i = thread.length - 1; i >= 0; i--) {
      const urls = thread[i].referenceImageUrls;
      if (urls && urls.length > 0) return urls;
    }
    return [] as string[];
  }, [thread]);
  const [includeAncestorRefs, setIncludeAncestorRefs] = useState(true);
  const [chatHasAnnotation, setChatHasAnnotation] = useState(false);
  const [chatSending, setChatSending]     = useState(false);
  const [chatError, setChatError]         = useState('');
  const [chatMode, setChatMode]                   = useState<InstructionMode>('precise');
  const [chatInterpretSummary, setChatInterpretSummary] = useState('');

  // Annotation queue for chat ref images
  const [annotationQueue, setAnnotationQueue]   = useState<PickedImage[]>([]);
  const [pendingAnnotated, setPendingAnnotated] = useState<PickedImage[]>([]);

  // Object selection on the latest result (Select objects in chat)
  const [chatObjPickerOpen, setChatObjPickerOpen] = useState(false);
  const [chatObjects, setChatObjects]             = useState<DetectedObject[] | null>(null);
  const [chatObjLoading, setChatObjLoading]       = useState(false);
  const [chatObjError, setChatObjError]           = useState('');
  const [chatSelectedIds, setChatSelectedIds]     = useState<Set<string>>(new Set());

  const inputRef     = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!chatInput.trim()) { setChatInterpretSummary(''); return; }
    const timer = setTimeout(() => {
      const result = detectMode(chatInput);
      setChatMode(result.mode);
      setChatInterpretSummary(result.summary);
    }, 400);
    return () => clearTimeout(timer);
  }, [chatInput]);

  // New iteration → the picker's target image changed; drop stale detections.
  useEffect(() => {
    setChatObjects(null);
    setChatSelectedIds(new Set());
    setChatObjPickerOpen(false);
  }, [lastGen.id]);

  const runChatDetection = async (force = false) => {
    setChatObjLoading(true);
    setChatObjError('');
    try {
      const objects = await detectObjects(
        projectId,
        { type: 'generation', generationId: lastGen.id },
        force,
      );
      setChatObjects(objects);
      if (force) setChatSelectedIds(new Set());
    } catch {
      setChatObjError('Could not identify objects — please try again.');
    } finally {
      setChatObjLoading(false);
    }
  };

  const openChatObjectPicker = () => {
    setChatObjPickerOpen(true);
    if (chatObjects === null && !chatObjLoading) runChatDetection();
  };

  const toggleChatObject = (objectId: string) => {
    setChatSelectedIds(prev => {
      const next = new Set(prev);
      next.has(objectId) ? next.delete(objectId) : next.add(objectId);
      return next;
    });
  };

  const date = new Date(rootGen.createdAt).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });

  // File picker → start annotation queue
  const handleAttachFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files  = Array.from(e.target.files || []);
    const slots  = 3 - chatRefImages.length;
    const picked = files.slice(0, slots).map(f => ({ file: f, url: URL.createObjectURL(f) }));
    e.target.value = '';
    if (picked.length === 0) return;
    setAnnotationQueue(picked);
    setPendingAnnotated([]);
  };

  // Annotator: confirmed (with or without markings)
  const handleAnnotationConfirm = (result: AnnotationResult) => {
    const newPending  = [...pendingAnnotated, { file: result.file, url: result.url }];
    const newHasAnno  = chatHasAnnotation || result.hasAnnotation;
    const [, ...rest] = annotationQueue;
    if (rest.length > 0) {
      setAnnotationQueue(rest);
      setPendingAnnotated(newPending);
      setChatHasAnnotation(newHasAnno);
    } else {
      setChatRefImages(prev => [...prev, ...newPending].slice(0, 3));
      setChatHasAnnotation(newHasAnno);
      setAnnotationQueue([]);
      setPendingAnnotated([]);
    }
  };

  // Annotator: cancel entire batch
  const handleAnnotationCancel = () => {
    annotationQueue.forEach(img => URL.revokeObjectURL(img.url));
    pendingAnnotated.forEach(img => URL.revokeObjectURL(img.url));
    setAnnotationQueue([]);
    setPendingAnnotated([]);
  };

  const removeRefImage = (index: number) => {
    setChatRefImages(prev => {
      URL.revokeObjectURL(prev[index].url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSend = async () => {
    const text = chatInput.trim();
    if (!text || chatSending) return;
    setChatSending(true);
    setChatError('');
    const imagesToSend   = chatRefImages;
    try {
      let annotationFlag = chatHasAnnotation;
      let annotatedFile: File | undefined;
      let finalText = text;

      const selected = (chatObjects ?? []).filter(o => chatSelectedIds.has(o.id));
      if (selected.length > 0) {
        const unnamed = selected.filter(
          o => !finalText.toLowerCase().includes(o.label.toLowerCase()),
        );
        if (unnamed.length > 0) {
          const regionList = unnamed.map(o => o.label).join(', ');
          finalText = `${finalText} (focus specifically on the marked region${unnamed.length > 1 ? 's' : ''}: ${regionList})`;
        }
        const token = localStorage.getItem('accessToken');
        const resp  = await fetch(`${API}/generations/${lastGen.id}/image`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!resp.ok) throw new Error('Could not load the image for object marking');
        const blob   = await resp.blob();
        const burned = await burnDetectedObjects(blob, selected);
        annotatedFile  = burned.file;
        annotationFlag = true;
      }

      const inheritRefs = imagesToSend.length === 0 && ancestorRefUrls.length > 0 && includeAncestorRefs;
      await onChatSubmit(lastGen.id, finalText, chatMode, imagesToSend, annotationFlag, inheritRefs, annotatedFile);
      setChatInput('');
      setChatRefImages([]);
      setIncludeAncestorRefs(true);
      setChatHasAnnotation(false);
      setChatSelectedIds(new Set());
      setChatObjPickerOpen(false);
    } catch (err: any) {
      if (!(err instanceof HandledGenerationError)) {
        setChatError(err.response?.data?.message || err.message || 'Failed to send');
      }
    } finally {
      setChatSending(false);
    }
  };

  return (
    <div className="result-card">
      {/* ── Header ── */}
      <div
        className="result-card-header"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && onToggle()}
        aria-expanded={isExpanded}
      >
        <div className="result-card-title">
          <h4>{rootGen.productDescription}</h4>
          <p>{date}{thread.length > 1 ? ` · ${thread.length} iterations` : ''}</p>
        </div>
        <div className="result-card-right">
          {confirmDelete ? (
            <div className="delete-confirm-row" onClick={e => e.stopPropagation()}>
              <span className="caption" style={{ color: 'var(--error)' }}>Delete?</span>
              <button
                className="delete-confirm-yes"
                disabled={deleting}
                onClick={async e => {
                  e.stopPropagation();
                  setDeleting(true);
                  await onDelete(rootGen.id);
                  setDeleting(false);
                  setConfirmDelete(false);
                }}
              >{deleting ? '…' : 'Yes'}</button>
              <button className="delete-confirm-no" onClick={e => { e.stopPropagation(); setConfirmDelete(false); }}>No</button>
            </div>
          ) : (
            <button
              className="result-delete-btn"
              onClick={e => { e.stopPropagation(); setConfirmDelete(true); }}
              aria-label="Delete this result"
              title="Delete"
            >
              <Trash size={15} />
            </button>
          )}
          <span className={`chevron${isExpanded ? ' open' : ''}`}>
            <ChevronDown size={18} />
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="result-card-body">

          {/* ── Root generation instruction ── */}
          {rootGen.productDescription && (
            <div className="result-instructions">
              "{rootGen.productDescription}"
            </div>
          )}

          {/* ── Root result: before/after slider ── */}
          {rootGen.status === 'completed' && rootGen.resultImageUrl ? (
            <>
              <BeforeAfterSlider
                beforeUrl={originalImageUrl}
                afterUrl={rootGen.resultImageUrl}
                beforeOptions={beforeOptions}
                onImageClick={onImageClick}
              />

              {/* Reference images */}
              {rootGen.referenceImageUrls && rootGen.referenceImageUrls.length > 0 && (
                <div className="result-ref-images">
                  <p className="result-ref-label">Reference Images Used</p>
                  <div className="result-ref-grid">
                    {rootGen.referenceImageUrls.map((url, i) => (
                      <img key={i} src={url} alt={`Reference ${i + 1}`} />
                    ))}
                  </div>
                </div>
              )}

              {/* Download root */}
              <div className="download-row">
                {originalImageUrl && (
                  <button className="download-btn" onClick={() => downloadImage(originalImageUrl, 'original-space.jpg', 'original')}>
                    <Download size={14} />Original
                  </button>
                )}
                <button className="download-btn" onClick={() => downloadImage(rootGen.resultImageUrl!, 'ai-preview-1.jpg', 'root')}>
                  <Download size={14} />AI Preview
                </button>
                <button className="download-btn" onClick={() => shareImage(rootGen.resultImageUrl!, 'ai-preview-1.jpg', rootGen.id, 'root')}>
                  <Share size={14} />Share
                </button>
                <button
                  className="download-btn"
                  onClick={() => onRetry(rootGen.id)}
                  title={rootGen.hasAnnotation ? 'Retries without the original markup — reselect regions if needed' : undefined}
                >
                  <RefreshCw size={14} />Retry
                </button>
              </div>
            </>
          ) : (rootGen.status === 'processing' || rootGen.status === 'pending') ? (
            <div className="processing-card">
              <div className="spinner" />
              <p className="body-sm">Generating your AI preview…</p>
              <p className="caption">This usually takes 20–60 seconds</p>
            </div>
          ) : rootGen.status === 'failed' ? (
            <div className="alert alert-error">
              <AlertCircle size={16} style={{ flexShrink: 0 }} />
              <span>Generation failed. Please try again.</span>
              <button className="btn-secondary" style={{ marginLeft: 'auto' }} onClick={() => onRetry(rootGen.id)}>
                <RefreshCw size={14} />Regenerate
              </button>
            </div>
          ) : null}

          {/* ── Chat thread (iterations after the root) ── */}
          {thread.length > 1 && rootGen.status === 'completed' && (
            <div className="chat-thread">
              <div className="chat-thread-divider">
                <span>AI Chat Iterations</span>
              </div>

              {thread.slice(1).map((gen, idx) => {
                const prevGen = thread[idx]; // generation before this one
                return (
                  <div key={gen.id} className="chat-turn">
                    {/* Reference images attached to this iteration */}
                    {gen.referenceImageUrls && gen.referenceImageUrls.length > 0 && (
                      <div className="chat-ref-row">
                        {gen.referenceImageUrls.map((url, i) => (
                          <img
                            key={i}
                            src={url}
                            alt={`Reference ${i + 1}`}
                            className="chat-ref-thumb-img"
                            onClick={() => onImageClick({ url, alt: `Reference ${i + 1}` })}
                          />
                        ))}
                      </div>
                    )}

                    {/* User instruction bubble */}
                    <div className="chat-bubble-row">
                      <div className="chat-bubble">
                        {gen.userInstructions || gen.productDescription}
                      </div>
                    </div>

                    {/* AI result — before/after slider vs previous iteration */}
                    {gen.status === 'completed' && gen.resultImageUrl ? (
                      <div className="chat-result">
                        <BeforeAfterSlider
                          beforeUrl={prevGen.resultImageUrl}
                          afterUrl={gen.resultImageUrl}
                          beforeOptions={beforeOptions}
                          onImageClick={onImageClick}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, width: '100%' }}>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              className="download-btn"
                              onClick={() => downloadImage(gen.resultImageUrl!, `ai-preview-${idx + 2}.jpg`, 'followup')}
                            >
                              <Download size={14} />Download
                            </button>
                            <button
                              className="download-btn"
                              onClick={() => shareImage(gen.resultImageUrl!, `ai-preview-${idx + 2}.jpg`, gen.id, 'followup')}
                            >
                              <Share size={14} />Share
                            </button>
                            <button
                              className="download-btn"
                              onClick={() => onRetry(gen.id)}
                              title={gen.hasAnnotation ? 'Retries without the original markup — reselect regions if needed' : undefined}
                            >
                              <RefreshCw size={14} />Retry
                            </button>
                          </div>
                          <span className="caption" style={{ paddingRight: 2 }}>
                            {new Date(gen.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                    ) : gen.status === 'processing' || gen.status === 'pending' ? (
                      <div className="chat-result chat-result-loading">
                        <div className="spinner" />
                        <p className="caption">Generating…</p>
                      </div>
                    ) : gen.status === 'failed' ? (
                      <div className="alert alert-error" style={{ marginTop: 8 }}>
                        <AlertCircle size={14} style={{ flexShrink: 0 }} />
                        <span>Generation failed.</span>
                        <button className="btn-secondary" style={{ marginLeft: 'auto' }} onClick={() => onRetry(gen.id)}>
                          <RefreshCw size={14} />Regenerate
                        </button>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Chat input (shown when last gen is completed) ── */}
          {canChat && (
            <div className="chat-input-section">
              {thread.length === 1 && (
                <div className="chat-thread-divider">
                  <span>Continue with AI</span>
                </div>
              )}
              {chatError && (
                <div className="alert alert-error" style={{ marginBottom: 8 }}>
                  <AlertCircle size={14} style={{ flexShrink: 0 }} />
                  <span>{chatError}</span>
                </div>
              )}

              {/* Reference image thumbnails */}
              {chatRefImages.length > 0 && (
                <div className="chat-ref-strip">
                  {chatRefImages.map((img, i) => (
                    <div key={i} className="chat-ref-thumb">
                      <img src={img.url} alt={`Reference ${i + 1}`} />
                      <button
                        className="chat-ref-remove"
                        onClick={() => removeRefImage(i)}
                        aria-label="Remove image"
                      >×</button>
                    </div>
                  ))}
                  {chatRefImages.length < 3 && (
                    <button
                      className="chat-ref-add"
                      onClick={() => fileInputRef.current?.click()}
                      aria-label="Add more reference images"
                    >+</button>
                  )}
                </div>
              )}

              {/* Inherited references from an earlier turn — reused unless dismissed */}
              {chatRefImages.length === 0 && ancestorRefUrls.length > 0 && includeAncestorRefs && (
                <div className="chat-ref-strip chat-ref-strip--inherited">
                  {ancestorRefUrls.map((url, i) => (
                    <div key={i} className="chat-ref-thumb chat-ref-thumb--inherited" title="Reference from an earlier step — will be reused">
                      <img src={url} alt={`Inherited reference ${i + 1}`} />
                    </div>
                  ))}
                  <button
                    type="button"
                    className="chat-inherited-clear"
                    onClick={() => setIncludeAncestorRefs(false)}
                    aria-label="Don't reuse earlier reference images"
                  >Don't reuse ×</button>
                </div>
              )}

              {/* Select objects on the latest result */}
              {chatObjPickerOpen && lastGen.resultImageUrl && (
                <ObjectSelector
                  imageUrl={lastGen.resultImageUrl}
                  objects={chatObjects}
                  selectedIds={chatSelectedIds}
                  onToggle={toggleChatObject}
                  loading={chatObjLoading}
                  error={chatObjError}
                  onRefresh={() => runChatDetection(true)}
                />
              )}

              <div className="chat-composer">
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleAttachFiles}
                />
                <textarea
                  ref={inputRef}
                  className="chat-input chat-input--bare"
                  placeholder="Give further instructions… (e.g. Make the sofa darker, add a floor lamp)"
                  value={chatInput}
                  onChange={e => { setChatInput(e.target.value); if (chatError) setChatError(''); }}
                  rows={2}
                  disabled={chatSending}
                />
                <div className="chat-composer-bar">
                  <div className="chat-composer-actions">
                    <button
                      type="button"
                      className="chat-attach-btn"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={chatSending || chatRefImages.length >= 3}
                      aria-label="Attach reference image"
                      title="Add reference image"
                    >
                      <AttachIcon />
                    </button>
                    <span className="chat-composer-divider" aria-hidden="true" />
                    <button
                      type="button"
                      className="chat-attach-btn"
                      onClick={() => {
                        if (chatObjPickerOpen) {
                          setChatObjPickerOpen(false);
                          setChatSelectedIds(new Set());
                        } else {
                          openChatObjectPicker();
                        }
                      }}
                      disabled={chatSending}
                      aria-label="Select objects to change"
                      title="Select objects to change"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2"/>
                        <rect x="8" y="8" width="8" height="8" rx="1"/>
                      </svg>
                    </button>
                  </div>
                  <button
                    className="chat-send-btn"
                    onClick={handleSend}
                    disabled={!chatInput.trim() || chatSending}
                    aria-label="Send instruction"
                  >
                    {chatSending ? <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> : <SendIcon />}
                  </button>
                </div>
              </div>
              {chatInterpretSummary && (
                <div className="instruction-preview">
                  {chatMode === 'precise' ? '🎯' : '✨'} {chatInterpretSummary}
                </div>
              )}
            </div>
          )}

        </div>
      )}

      {/* Annotation modal for chat ref images */}
      {annotationQueue.length > 0 && (
        <ImageAnnotator
          imageFile={annotationQueue[0].file}
          imageUrl={annotationQueue[0].url}
          title={`Mark area in reference image${annotationQueue.length > 1 ? ` (${pendingAnnotated.length + 1}/${pendingAnnotated.length + annotationQueue.length})` : ''}`}
          onConfirm={handleAnnotationConfirm}
          onCancel={handleAnnotationCancel}
        />
      )}
    </div>
  );
}



/* ─── Main Page ───────────────────────────────────────────────── */
function ProjectDetail() {
  const { user } = useAuth();
  const isGuestUser = user?.role === 'guest';
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [project, setProject]         = useState<Project | null>(null);
  const [loading, setLoading]         = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [pollingIds, setPollingIds]   = useState<Set<string>>(new Set());
  const [imageModal, setImageModal]   = useState<{ url: string; alt: string } | null>(null);

  const resultsRef           = useRef<HTMLDivElement>(null);
  const initialExpandDone    = useRef(false);
  const annotationRestored   = useRef(false);

  // form state
  const [spaceImage, setSpaceImage]             = useState<PickedImage | null>(null);
  const [spaceHasAnnotation, setSpaceHasAnnotation] = useState(false);
  const [pendingSpaceAnnotation, setPendingSpaceAnnotation] = useState<PickedImage | null>(null);
  const [instruction, setInstruction]           = useState('');
  const [activeChip, setActiveChip]             = useState<'replace' | 'add' | 'remove' | 'restyle' | null>(null);
  const [detectedMode, setDetectedMode]         = useState<InstructionMode>('precise');
  const [interpretSummary, setInterpretSummary] = useState('');
  const [refImages, setRefImages]       = useState<PickedImage[]>([]);

  // Most recent "Shop the look" add — drives the Swap/Add quick-prompt chips
  // above the instruction box so the user isn't retyping the same sentence
  // for every reference product. Cleared on pick, dismiss, or form Clear.
  const [lastAddedProduct, setLastAddedProduct] = useState<Product | null>(null);

  // Object URLs of refImages that came from "Add product" (as opposed to a
  // manual upload or the Explore inspiration image) — a product photo is
  // self-explanatory enough that typing an instruction becomes optional.
  const [productRefUrls, setProductRefUrls]     = useState<Set<string>>(new Set());
  const hasProductRef = refImages.some(img => productRefUrls.has(img.url));

  // Detected-object dots on the original image
  const [detObjects, setDetObjects]             = useState<DetectedObject[] | null>(null);
  const [detLoading, setDetLoading]             = useState(false);
  const [detError, setDetError]                 = useState('');
  const [selectedObjectIds, setSelectedObjectIds] = useState<Set<string>>(new Set());

  // "Add product" panel — gated on the room containing anything shoppable.
  // Memoized because it is a prop of ProductPanel, which refetches on identity change.
  const [showProductPanel, setShowProductPanel] = useState(false);
  const shopLabels = useMemo(() => shoppableLabels(detObjects), [detObjects]);

  // Load inspiration image from Explore → /new-project → here via location.state
  const inspirationUrl = (location.state as any)?.inspirationUrl as string | undefined;
  const inspirationLoaded = useRef(false);

  useEffect(() => {
    if (!inspirationUrl || inspirationLoaded.current) return;
    inspirationLoaded.current = true;

    (async () => {
      try {
        const picked = await fetchAsPickedImage(inspirationUrl, 'inspiration');
        setRefImages(prev => [...prev, picked]);
      } catch {
        // Best-effort — ignore silently
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addProductReference = async (product: Product) => {
    if (refImages.length >= MAX_REFERENCE_IMAGES) {
      throw new Error(`Reference image limit reached (${MAX_REFERENCE_IMAGES} max)`);
    }
    const src = product.imageUrls[0];
    if (!src) throw new Error('Product has no image to add as a reference.');
    const picked = await fetchAsPickedImage(src, `product-${product.id}`);
    // Re-check the cap inside the updater (not just the closure read above) so
    // two concurrent adds can't both pass a stale length check and land the
    // array over the cap — React applies updaters against the latest state.
    let added = false;
    setRefImages(prev => {
      if (prev.length >= MAX_REFERENCE_IMAGES) return prev;
      added = true;
      return [...prev, picked];
    });
    if (!added) throw new Error(`Reference image limit reached (${MAX_REFERENCE_IMAGES} max)`);
    setProductRefUrls(prev => new Set(prev).add(picked.url));
    setLastAddedProduct(product);
  };

  /** Swap/Add quick-prompt chip → phrases and applies the instruction for the
   * most recently added product, same precise-mode chip machinery as the
   * (currently trigger-less) activeChip state used for mode detection above. */
  const applyProductChip = (kind: 'replace' | 'add') => {
    if (!lastAddedProduct) return;
    const word = productWord(lastAddedProduct);
    const phrase = kind === 'replace'
      ? `Replace the existing ${word} with this one.`
      : `Add this ${word} to the room.`;
    setInstruction(prev => {
      const trimmed = prev.trim();
      return trimmed ? `${trimmed} ${phrase}` : phrase;
    });
    setActiveChip(kind);
    setLastAddedProduct(null);
    if (genError) setGenError('');
  };

  const [generating, setGenerating]     = useState(false);
  const [genError, setGenError]         = useState('');

  // region marking on locked original image
  const [annotatedSpace, setAnnotatedSpace]         = useState<PickedImage | null>(null);
  const [annotationCanvasDims, setAnnotationCanvasDims] = useState<{ w: number; h: number } | null>(null);
  const [fetchingAnnotate, setFetchingAnnotate]     = useState(false);
  const [pendingLockedAnnotation, setPendingLockedAnnotation] = useState<PickedImage | null>(null);
  const [showRegionNotice, setShowRegionNotice]     = useState(false);

  useEffect(() => {
    if (!annotatedSpace) return;
    setShowRegionNotice(true);
    const t = setTimeout(() => setShowRegionNotice(false), 2000);
    return () => clearTimeout(t);
  }, [annotatedSpace]);

  // credit balance — for out-of-credits gate
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [showOOC, setShowOOC] = useState(false);

  // saved labeled regions (persisted per project in localStorage)
  const [savedRegions, setSavedRegions] = useState<LabeledRegion[]>(() => {
    try {
      const stored = localStorage.getItem(`co-regions-${id}`);
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  // content policy acknowledgment (one-time per session)
  const [policyAcked, setPolicyAcked] = useState(() => !!sessionStorage.getItem('co_policy_ack'));

  useEffect(() => {
    if (!instruction.trim()) {
      setInterpretSummary('');
      return;
    }
    const chipMode = activeChip
      ? (activeChip === 'restyle' ? 'creative' : 'precise') as InstructionMode
      : undefined;
    const timer = setTimeout(() => {
      const result = detectMode(instruction, chipMode);
      setDetectedMode(result.mode);
      setInterpretSummary(result.summary);
    }, 400);
    return () => clearTimeout(timer);
  }, [instruction, activeChip]);

  /* fetch project */
  const fetchProject = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects/${id}`);
      const data: Project = res.data;
      setProject(data);
    } catch {
      // leave project as null → "not found" shown
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { if (id) fetchProject(); }, [id, fetchProject]);

  useEffect(() => {
    axios.get<{ balance: string }>(`${API}/credits`)
      .then(res => setCreditBalance(Number(res.data.balance)))
      .catch(() => {});
  }, []);

  // Auto-expand the newest root generation — runs only once on initial project load.
  // handleGenerate explicitly sets expandedIds for new roots so this doesn't need to re-run.
  useEffect(() => {
    if (initialExpandDone.current || !project) return;
    const roots = project.generations.filter(g => !g.parentGenerationId);
    if (roots.length) {
      const newest = roots.reduce((a, b) =>
        new Date(a.createdAt) > new Date(b.createdAt) ? a : b
      );
      setExpandedIds(new Set([newest.id]));
      initialExpandDone.current = true;
    }
  }, [project]);

  // Restore annotated-space image from localStorage regions after a page refresh.
  useEffect(() => {
    if (annotationRestored.current || annotatedSpace || savedRegions.length === 0) return;
    if (!project?.id || !project?.originalImageUrl) return;
    annotationRestored.current = true;

    (async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const resp  = await fetch(`${API}/projects/${project.id}/original-image`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!resp.ok) return;
        const blob = await resp.blob();
        const { file, url, canvasDims } = await burnRegionsOntoImage(blob, savedRegions);
        setAnnotationCanvasDims(canvasDims);
        setAnnotatedSpace({ file, url });
      } catch {
        // silently fail — user can re-mark manually
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, project?.originalImageUrl]);

  /* polling — poll all active generation IDs */
  const pollGeneration = useCallback(async (gId: string) => {
    try {
      const res     = await axios.get(`${API}/generations/${gId}`);
      const updated: Generation = res.data;
      setProject(prev => {
        if (!prev) return prev;
        const exists = prev.generations.find(g => g.id === gId);
        const gens   = exists
          ? prev.generations.map(g => g.id === gId ? updated : g)
          : [...prev.generations, updated];
        return { ...prev, generations: gens };
      });
      if (updated.status === 'completed' || updated.status === 'failed') {
        setPollingIds(prev => { const s = new Set(prev); s.delete(gId); return s; });
      }
    } catch {
      setPollingIds(prev => { const s = new Set(prev); s.delete(gId); return s; });
    }
  }, []);

  useEffect(() => {
    if (!pollingIds.size) return;
    const interval = setInterval(() => {
      Array.from(pollingIds).forEach(gId => pollGeneration(gId));
    }, 2000);
    return () => clearInterval(interval);
  }, [pollingIds, pollGeneration]);

  /* toggle expand */
  const toggleExpand = (rootId: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(rootId) ? next.delete(rootId) : next.add(rootId);
      return next;
    });
  };

  /* fetch locked original image as File for annotation */
  const handleOpenMarkRegion = useCallback(async () => {
    if (!project?.originalImageUrl) return;
    setFetchingAnnotate(true);
    try {
      const token = localStorage.getItem('accessToken');
      const resp = await fetch(`${API}/projects/${project.id}/original-image`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error('Failed to fetch image');
      const blob = await resp.blob();
      const file = new File([blob], 'original-space.jpg', { type: blob.type || 'image/jpeg' });
      const url  = URL.createObjectURL(blob);
      setPendingLockedAnnotation({ file, url });
    } catch {
      setGenError('Could not open image for marking. Please try again.');
    } finally {
      setFetchingAnnotate(false);
    }
  }, [project?.originalImageUrl, project?.id]);

  /* run object detection on the project original */
  const runDetection = useCallback(async (force = false) => {
    if (!project?.id) return;
    setDetLoading(true);
    setDetError('');
    try {
      const objects = await detectObjects(project.id, { type: 'original' }, force);
      setDetObjects(objects);
      if (force) setSelectedObjectIds(new Set());
    } catch {
      setDetError('Could not identify objects — please try again.');
    } finally {
      setDetLoading(false);
    }
  }, [project?.id]);

  const toggleObjectSelection = useCallback((objectId: string) => {
    setSelectedObjectIds(prev => {
      const next = new Set(prev);
      next.has(objectId) ? next.delete(objectId) : next.add(objectId);
      return next;
    });
  }, []);

  // Auto-detect once the project has a saved original image
  useEffect(() => {
    if (detObjects === null && !detLoading && project?.originalImageUrl) {
      runDetection();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.originalImageUrl]);

  /* submit new root generation */
  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!project) return;
    if (!instruction.trim() && !hasProductRef) return;

    const existingImage = project.originalImageUrl;
    if (!existingImage && !spaceImage) {
      setGenError('Please upload an original space image first.');
      return;
    }

    // First-time policy acknowledgment gate
    if (!policyAcked) {
      setPolicyAcked(true);
      sessionStorage.setItem('co_policy_ack', '1');
      // show it, user already saw it — proceed
    }

    if (creditBalance !== null && creditBalance <= 0) {
      setShowOOC(true);
      if (isGuestUser) mixpanel.track('guest_signup_wall_shown', { project_id: project.id });
      return;
    }

    setGenerating(true);
    setGenError('');

    try {
      const formData = new FormData();

      const selected = (detObjects ?? []).filter(o => selectedObjectIds.has(o.id));

      if (selected.length > 0 && project.originalImageUrl) {
        // ── Objects selected: burn their boxes into the original ──
        // Blank only reaches here when hasProductRef gated it past the guard above.
        let finalInstruction = instruction.trim() || DEFAULT_PRODUCT_INSTRUCTION;
        const unnamed = selected.filter(
          o => !finalInstruction.toLowerCase().includes(o.label.toLowerCase()),
        );
        if (unnamed.length > 0) {
          const regionList = unnamed.map(o => o.label).join(', ');
          finalInstruction = `${finalInstruction} (focus specifically on the marked region${unnamed.length > 1 ? 's' : ''}: ${regionList})`;
        }
        formData.append('instruction', finalInstruction);
        formData.append('mode', detectedMode);
        formData.append('hasAnnotation', 'true');

        const token = localStorage.getItem('accessToken');
        const resp = await fetch(`${API}/projects/${project.id}/original-image`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!resp.ok) throw new Error('Could not load the original image');
        const blob = await resp.blob();
        const burned = await burnDetectedObjects(blob, selected);
        formData.append('annotatedImage', burned.file);
      } else {
        // ── No objects selected: existing describe behaviour, unchanged ──
        const useAnnotated = !!annotatedSpace?.file;

        // Blank only reaches here when hasProductRef gated it past the guard above.
        let finalInstruction = instruction.trim() || DEFAULT_PRODUCT_INSTRUCTION;
        if (useAnnotated && savedRegions.length > 0) {
          const unlabeled = savedRegions.filter(r => !finalInstruction.includes(r.label));
          if (unlabeled.length > 0) {
            const regionList = unlabeled.map(r => r.label).join(', ');
            finalInstruction = `${finalInstruction} (focus specifically on the marked region${unlabeled.length > 1 ? 's' : ''}: ${regionList})`;
          }
        }
        formData.append('instruction', finalInstruction);
        formData.append('mode', detectedMode);
        const sendingAnnotatedImage = useAnnotated && !existingImage;
        formData.append('hasAnnotation', (spaceHasAnnotation || sendingAnnotatedImage) ? 'true' : 'false');

        if (sendingAnnotatedImage) {
          if (spaceImage?.file) formData.append('originalImage', spaceImage.file);
          formData.append('annotatedImage', annotatedSpace!.file);
        } else if (!existingImage && spaceImage?.file) {
          formData.append('originalImage', spaceImage.file);
        }
      }

      refImages.forEach((img, i) => {
        if (img.file) formData.append(`referenceImage_${i}`, img.file);
      });

      const res    = await axios.post(`${API}/projects/${project.id}/generate`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Idempotency-Key': genUUID(),
        },
      });
      const newGen: Generation = res.data;

      if (!existingImage && spaceImage) {
        setProject(prev => prev ? { ...prev, originalImageUrl: spaceImage.url } : prev);
      }

      setProject(prev => {
        if (!prev) return prev;
        return { ...prev, generations: [...prev.generations, newGen] };
      });

      setExpandedIds(new Set([newGen.id]));
      setPollingIds(prev => { const s = new Set(Array.from(prev)); s.add(newGen.id); return s; });

      setInstruction('');
      setActiveChip(null);
      setInterpretSummary('');
      setRefImages([]);
      setSelectedObjectIds(new Set());
      setCreditBalance(prev => prev !== null ? Math.max(0, prev - 1) : prev);

      // Scroll to the results section so the user sees the new generation card
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    } catch (err: any) {
      if (err.response?.status === 402) {
        setShowOOC(true);
        if (isGuestUser) mixpanel.track('guest_signup_wall_shown', { project_id: project.id });
      } else {
        setGenError(err.response?.data?.message || err.response?.data?.error || 'Failed to generate preview. Please try again.');
      }
    } finally {
      setGenerating(false);
    }
  };

  /* delete entire generation thread */
  const handleDeleteThread = useCallback(async (rootId: string) => {
    await axios.delete(`${API}/generations/${rootId}`);
    setProject(prev => {
      if (!prev) return prev;
      const thread = buildThread(prev.generations, rootId);
      const threadIds = new Set(thread.map(g => g.id));
      return { ...prev, generations: prev.generations.filter(g => !threadIds.has(g.id)) };
    });
  }, []);

  /* retry/regenerate a single generation in place */
  const handleRetry = useCallback(async (generationId: string) => {
    if (!project) return;

    const prevStatus = project.generations.find(g => g.id === generationId)?.status;

    setProject(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        generations: prev.generations.map(g =>
          g.id === generationId ? { ...g, status: 'processing' as const } : g,
        ),
      };
    });

    try {
      await axios.post(`${API}/generations/${generationId}/retry`, null, {
        headers: { 'Idempotency-Key': genUUID() },
      });
      setPollingIds(prev => { const s = new Set(prev); s.add(generationId); return s; });
    } catch (err: any) {
      // 402 is the only case where the backend actually transitioned the row
      // to FAILED (credit deduction ran after the retry claim) — every other
      // rejection (409 not-retryable, 400 missing parent, 404, network) never
      // touched the row server-side, so restore what was on screen before the
      // optimistic update instead of clobbering a still-valid completed image.
      if (err.response?.status === 402) {
        setShowOOC(true);
        if (isGuestUser) mixpanel.track('guest_signup_wall_shown', { project_id: project.id });
      }
      const revertStatus = err.response?.status === 402 ? 'failed' as const : (prevStatus ?? 'failed' as const);
      setProject(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          generations: prev.generations.map(g =>
            g.id === generationId ? { ...g, status: revertStatus } : g,
          ),
        };
      });
    }
  }, [project, isGuestUser]);

  /* submit chat continuation */
  const handleChatSubmit = useCallback(async (
    parentId: string,
    instruction: string,
    mode: InstructionMode,
    refImages: PickedImage[],
    hasAnnotation: boolean,
    inheritRefs: boolean,
    annotatedImage?: File,
  ) => {
    if (!project) return;

    const formData = new FormData();
    formData.append('parentGenerationId', parentId);
    formData.append('instruction', instruction);
    formData.append('mode', mode);
    formData.append('hasAnnotation', hasAnnotation ? 'true' : 'false');
    formData.append('inheritRefs', inheritRefs ? 'true' : 'false');
    if (annotatedImage) formData.append('annotatedImage', annotatedImage);
    refImages.forEach((img, i) => {
      if (img.file) formData.append(`referenceImage_${i}`, img.file);
    });

    try {
      const res    = await axios.post(`${API}/projects/${project.id}/generate`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Idempotency-Key': genUUID(),
        },
      });
      const newGen: Generation = res.data;

      setProject(prev => {
        if (!prev) return prev;
        return { ...prev, generations: [...prev.generations, newGen] };
      });

      setPollingIds(prev => { const s = new Set(Array.from(prev)); s.add(newGen.id); return s; });
    } catch (err: any) {
      if (err.response?.status === 402) {
        setShowOOC(true);
        if (isGuestUser) mixpanel.track('guest_signup_wall_shown', { project_id: project.id });
        throw new HandledGenerationError();
      }
      throw err;
    }
  }, [project, isGuestUser]);

  /* ── Render ──────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Loading project…</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="loading-screen">
        <AlertCircle size={40} color="var(--error)" />
        <p style={{ color: 'var(--error)' }}>Project not found.</p>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/home')} style={{ marginTop: 8 }}>
          Back to Home
        </button>
      </div>
    );
  }

  // Derive root generations + threads
  const roots = project.generations
    .filter(g => !g.parentGenerationId)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const hasOriginalImage = !!project.originalImageUrl;
  // A product reference is self-explanatory enough that the instruction
  // becomes optional — the backend still needs *some* text, so handleGenerate
  // falls back to DEFAULT_PRODUCT_INSTRUCTION when it's left blank.
  const canGenerate = (hasOriginalImage || !!spaceImage) && (!!instruction.trim() || hasProductRef);

  // All images that can be used as the "before" side of any slider —
  // original space (mandatory) + the 3 most-recent completed previews.
  const recentPreviews = project.generations
    .filter(g => g.status === 'completed' && g.resultImageUrl)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 3)
    .reverse();

  const beforeOptions: BeforeOption[] = [
    ...(project.originalImageUrl ? [{ url: project.originalImageUrl, label: 'Original Space' }] : []),
    ...recentPreviews.map((g, i) => ({ url: g.resultImageUrl!, label: `Preview ${i + 1}` })),
  ];

  return (
    <div className="project-page">
      {/* Header */}
      <header className="app-header">
        <div className="header-inner">
          <button className="header-action-btn" onClick={() => navigate(-1)} aria-label="Go back">
            <ArrowLeft size={20} />
          </button>
          <div className="logo-lockup" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
            <div className="logo-mark-sm"><CoArchitectLogo size={28} /></div>
            <span className="heading-brand logo-text-sm" style={{ fontSize: '0.75rem' }}>{project.name.toUpperCase()}</span>
          </div>
          <div className="header-actions-row">
            <button className="header-action-btn" onClick={() => navigate('/home')} aria-label="Home" title="Home">
              <Home size={18} />
            </button>
          </div>
        </div>
      </header>

      <div className="page-scroll">
        <div className="project-page-content">

          {/* ── Generate Form ──────────────────────────────────── */}
          <form onSubmit={handleGenerate} noValidate>
            <div className="generate-form-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <Sparkles size={18} color="var(--accent)" />
                <h3 className="heading-sm">Generate Preview</h3>
              </div>

              {genError && (
                <div className="alert alert-error" role="alert">
                  <AlertCircle size={15} style={{ flexShrink: 0 }} />
                  <span>{genError}</span>
                </div>
              )}

              {/* 1. Original Space Image */}
              {hasOriginalImage ? (
                <div className="form-field">
                  <span className="form-label">Original Space</span>
                  <div style={{ position: 'relative' }}>
                    <ObjectSelector
                      imageUrl={annotatedSpace?.url || project.originalImageUrl!}
                      objects={detObjects}
                      selectedIds={selectedObjectIds}
                      onToggle={toggleObjectSelection}
                      loading={detLoading}
                      error={detError}
                      onRefresh={() => runDetection(true)}
                    />
                    <button
                      type="button"
                      className="mark-region-btn mark-region-btn-overlay"
                      onClick={e => { e.stopPropagation(); handleOpenMarkRegion(); }}
                      disabled={fetchingAnnotate}
                      title={annotatedSpace ? 'Re-mark Region' : 'Mark Region to Update'}
                    >
                      {fetchingAnnotate
                        ? <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                        : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/>
                          </svg>
                      }
                      <span>{annotatedSpace ? 'Re-mark' : 'Mark Region'}</span>
                    </button>
                  </div>
                  {showRegionNotice && annotatedSpace && (
                    <div className="annotated-preview-strip">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      <span>Region marked — AI will focus on this area</span>
                    </div>
                  )}
                  {/* Always offered: with no shoppable object detected the panel
                      falls back to browsing the catalog, so there's nothing to gate on. */}
                  <button
                    type="button"
                    className="add-product-btn"
                    onClick={() => setShowProductPanel(true)}
                  >
                    <Sofa size={15} /> Add product
                  </button>
                </div>
              ) : (
                <>
                  <ImagePicker
                      value={spaceImage}
                      onChange={img => {
                        if (img) {
                          setPendingSpaceAnnotation(img);
                        } else {
                          setSpaceImage(null);
                          setSpaceHasAnnotation(false);
                        }
                      }}
                      onRemove={() => { setSpaceImage(null); setSpaceHasAnnotation(false); }}
                      label="Original Space Image"
                      hint="Take a photo of the room/space you want to transform"
                      required
                      id="originalSpace"
                    />

                  {/* Mark Region button — fallback for manual-upload path */}
                  {!hasOriginalImage && spaceImage && (
                    <div style={{ marginBottom: 12 }}>
                      <button
                        type="button"
                        className="mark-region-btn"
                        onClick={hasOriginalImage ? handleOpenMarkRegion : () => {
                          if (spaceImage) setPendingSpaceAnnotation(spaceImage);
                        }}
                        disabled={fetchingAnnotate}
                      >
                        {fetchingAnnotate
                          ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Loading…</>
                          : <>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/>
                              </svg>
                              {annotatedSpace ? 'Re-mark Region' : 'Mark Region to Update'}
                            </>
                        }
                      </button>
                      {annotatedSpace && (
                        <div className="annotated-preview-strip">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                          <span>Region marked — AI will focus on this area</span>
                          <button
                            type="button"
                            className="annotated-clear-btn"
                            onClick={() => {
                              if (annotatedSpace?.url) URL.revokeObjectURL(annotatedSpace.url);
                              setAnnotatedSpace(null);
                              setAnnotationCanvasDims(null);
                              setSavedRegions([]);
                              if (project) localStorage.removeItem(`co-regions-${project.id}`);
                            }}
                          >Clear</button>
                        </div>
                      )}
                      {savedRegions.length > 0 && (
                        <div className="region-chips-row">
                          <span className="caption" style={{ flexShrink: 0 }}>Regions:</span>
                          {savedRegions.map(r => (
                            <button
                              key={r.id}
                              type="button"
                              className="region-chip"
                              onClick={() => {
                                const ref = `[${r.label}]`;
                                setInstruction(prev => prev.includes(ref) ? prev : prev ? `${prev} ${ref}` : ref);
                              }}
                              title={`Insert "${r.label}" into instructions`}
                            >
                              {r.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* 2. Instruction field */}
              <div className="form-field">
                <label className="form-label" htmlFor="instruction">
                  What would you like to do?
                  {hasProductRef
                    ? <span className="caption" style={{ fontWeight: 400 }}> (optional with a product reference)</span>
                    : <span className="required">*</span>}
                </label>

                {lastAddedProduct && (
                  <div className="product-chip-prompt">
                    <span className="product-chip-prompt-label">
                      Added “{lastAddedProduct.name}” — swap it in for the existing piece, or just add it?
                    </span>
                    <div className="instruction-chips" style={{ marginBottom: 0 }}>
                      <button
                        type="button"
                        className={`chip${activeChip === 'replace' ? ' chip-active' : ''}`}
                        onClick={() => applyProductChip('replace')}
                      >
                        🔄 Swap existing
                      </button>
                      <button
                        type="button"
                        className={`chip${activeChip === 'add' ? ' chip-active' : ''}`}
                        onClick={() => applyProductChip('add')}
                      >
                        ➕ Add to room
                      </button>
                    </div>
                    <button
                      type="button"
                      className="product-chip-dismiss"
                      onClick={() => setLastAddedProduct(null)}
                      aria-label="Dismiss"
                    >
                      ✕
                    </button>
                  </div>
                )}

                <textarea
                  id="instruction"
                  className="form-textarea"
                  placeholder={
                    hasProductRef
                      ? 'e.g. Replace the floor seater with this · Add this next to the window — or leave blank to just add it as-is'
                      : 'e.g. Replace the floor seater with a brown bean bag · Give the room a cozy Japandi feel · Add a rattan pendant light above the table'
                  }
                  value={instruction}
                  onChange={e => {
                    setInstruction(e.target.value);
                    if (activeChip) setActiveChip(null);
                    if (genError) setGenError('');
                  }}
                  rows={4}
                  required={!hasProductRef}
                />

                {interpretSummary && (
                  <div className="instruction-preview">
                    {detectedMode === 'precise' ? '🎯' : '✨'} {interpretSummary}
                  </div>
                )}
              </div>

              {/* 4. Reference Images */}
              <MultiImagePicker
                values={refImages}
                onChange={setRefImages}
                label="Reference Images (optional)"
                maxImages={MAX_REFERENCE_IMAGES}
              />

              {/* Low-credits inline nudge — guests get the out-of-credits sheet's
                  sign-up wall instead; a guest starts with exactly one root
                  generation's worth of credits, so this would otherwise fire
                  before they've used their free preview at all. */}
              {creditBalance !== null && creditBalance <= 2 && creditBalance > 0 && !isGuestUser && (
                <div className="upgrade-inline" style={{ marginBottom: 12 }}>
                  <div className="upgrade-inline-text">
                    <span className="upgrade-inline-title">{creditBalance} credit{creditBalance !== 1 ? 's' : ''} left</span>
                    <span className="upgrade-inline-sub">Upgrade for 50 credits/mo</span>
                  </div>
                  <button className="upgrade-inline-btn" onClick={() => navigate('/credits')} type="button">
                    Upgrade
                  </button>
                </div>
              )}

              {/* Content policy notice */}
              <div className="policy-notice policy-notice-compact" role="note" title="No nudity or explicit content · No harmful or illegal edits · Violations may lead to access restriction">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: 'var(--warning)' }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                <div className="policy-notice-text">
                  <p>No explicit or harmful content — violations restrict access</p>
                </div>
              </div>

              {/* 5. Actions */}
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setInstruction('');
                    setActiveChip(null);
                    setInterpretSummary('');
                    setRefImages([]);
                    setGenError('');
                    setLastAddedProduct(null);
                    setProductRefUrls(new Set());
                    if (!hasOriginalImage) setSpaceImage(null);
                  }}
                  disabled={generating}
                  style={{ flex: 1 }}
                >
                  Clear
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={generating || !canGenerate}
                  style={{ flex: 2 }}
                >
                  {generating
                    ? <><span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> Generating…</>
                    : <><Sparkles size={16} />Generate Preview</>
                  }
                </button>
              </div>

              {showProductPanel && (
                <ProductPanel
                  labels={shopLabels}
                  onAddReference={addProductReference}
                  onClose={() => setShowProductPanel(false)}
                />
              )}
            </div>
          </form>

          {/* ── Results (always shown below the form) ─────────────── */}
          {roots.length > 0 && (
            <div className="results-section" ref={resultsRef}>
              <div className="results-section-title" style={{ marginBottom: 12 }}>
                <h3 className="heading-sm">Results</h3>
                <span className="caption">{roots.length} result{roots.length !== 1 ? 's' : ''}</span>
              </div>
              {roots.map(root => {
                const thread = buildThread(project.generations, root.id);
                return (
                  <ThreadCard
                    key={root.id}
                    thread={thread}
                    projectId={project.id}
                    originalImageUrl={project.originalImageUrl}
                    beforeOptions={beforeOptions}
                    isExpanded={expandedIds.has(root.id)}
                    onToggle={() => toggleExpand(root.id)}
                    onImageClick={setImageModal}
                    onChatSubmit={handleChatSubmit}
                    onDelete={handleDeleteThread}
                    onRetry={handleRetry}
                  />
                );
              })}
            </div>
          )}

          <div style={{ height: 40 }} />
        </div>
      </div>

      <ImageModal image={imageModal} onClose={() => setImageModal(null)} />

      {/* Out-of-credits blocking sheet */}
      {showOOC && (
        <div className="sheet-backdrop" onClick={() => setShowOOC(false)}>
          <div
            className="bottom-sheet"
            onClick={e => e.stopPropagation()}
            style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '0 20px 24px' }}
          >
            <div className="sheet-handle" />
            {isGuestUser ? (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <div className="oo-credits-icon">
                    <Sparkles size={22} color="var(--accent)" />
                  </div>
                  <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 500, margin: 0, color: 'var(--text)' }}>
                    Sign up to keep designing
                  </h3>
                  <p className="sheet-sub">You've used your free preview. Create an account to keep this project and generate more.</p>
                </div>
                <button
                  className="btn btn-primary"
                  style={{ width: '100%', padding: 13 }}
                  onClick={() => {
                    sessionStorage.setItem('guestReturnTo', `/project/${project.id}`);
                    sessionStorage.setItem('guestClaimUserId', user?.id ?? '');
                    navigate('/signup');
                  }}
                >
                  Sign Up Free
                </button>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <div className="oo-credits-icon">
                    <Sparkles size={22} color="var(--accent)" />
                  </div>
                  <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 500, margin: 0, color: 'var(--text)' }}>
                    You're out of credits
                  </h3>
                  <p className="sheet-sub">Upgrade to keep generating — or wait for monthly refresh.</p>
                </div>
                <div className="plan-tier plan-tier-selected">
                  <div>
                    <div className="plan-tier-name">Studio</div>
                    <div className="plan-tier-meta">50 credits/mo · priority queue</div>
                  </div>
                  <div className="plan-tier-price">₹999<small>/mo</small></div>
                </div>
                <button
                  className="btn btn-primary"
                  style={{ width: '100%', padding: 13 }}
                  onClick={() => navigate('/credits')}
                >
                  <Sparkles size={14} /> Upgrade to Studio
                </button>
                <button className="btn btn-ghost" style={{ width: '100%' }} onClick={() => navigate('/credits')}>
                  See all plans
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Annotation modal for new space image (not yet saved) */}
      {pendingSpaceAnnotation && (
        <ImageAnnotator
          imageFile={pendingSpaceAnnotation.file}
          imageUrl={pendingSpaceAnnotation.url}
          title="Mark the area to update"
          onConfirm={(result: AnnotationResult) => {
            setSpaceImage({ file: result.file, url: result.url });
            setSpaceHasAnnotation(result.hasAnnotation);
            setAnnotationCanvasDims(result.canvasDims);
            setPendingSpaceAnnotation(null);
            if (result.regions.length > 0 && project) {
              localStorage.setItem(`co-regions-${project.id}`, JSON.stringify(result.regions));
              setSavedRegions(result.regions);
            }
          }}
          onCancel={() => {
            URL.revokeObjectURL(pendingSpaceAnnotation.url);
            setPendingSpaceAnnotation(null);
          }}
        />
      )}

      {/* Annotation modal for locked original space image */}
      {pendingLockedAnnotation && (
        <ImageAnnotator
          imageFile={pendingLockedAnnotation.file}
          imageUrl={pendingLockedAnnotation.url}
          title="Mark region to update"
          initialRegions={savedRegions}
          onConfirm={(result: AnnotationResult) => {
            setPendingLockedAnnotation(null);
            if (result.hasAnnotation && result.regions.length > 0) {
              setAnnotatedSpace({ file: result.file, url: result.url });
              setAnnotationCanvasDims(result.canvasDims);
              if (project) {
                localStorage.setItem(`co-regions-${project.id}`, JSON.stringify(result.regions));
                setSavedRegions(result.regions);
              }
            } else {
              // User cleared all regions — wipe the stored annotation
              if (annotatedSpace?.url) URL.revokeObjectURL(annotatedSpace.url);
              setAnnotatedSpace(null);
              setAnnotationCanvasDims(null);
              setSavedRegions([]);
              if (project) localStorage.removeItem(`co-regions-${project.id}`);
            }
          }}
          onCancel={() => {
            URL.revokeObjectURL(pendingLockedAnnotation.url);
            setPendingLockedAnnotation(null);
          }}
        />
      )}
    </div>
  );
}

export default ProjectDetail;
